import { Button as BaseButton } from '@base-ui/react/button'
import { useRender } from '@base-ui/react/use-render'
import { forwardRef } from 'react'
import type { ComponentProps } from 'react'
import { IconArrowUpRight } from '../icons'
import type { ActionTone } from './styles/tones'
import type { ChipVariant } from './styles/chip-badge'
import { chipControlClasses } from './styles/chip-control'
import styles from './styles/chip-badge.module.css'

/**
 * Los dos chips que **sí** son controles.
 *
 * Viven en su propio archivo y no como una prop de `Chip` porque un control necesita tres
 * decisiones que un `<span>` no tiene: qué elemento emite, cómo se ve enfocado y qué pasa cuando
 * está deshabilitado. Meterlas en el chip inerte —que es la enorme mayoría de los usos— habría
 * cargado a todos con el peso de unos pocos.
 *
 * Comparten el peso, la geometría y el módulo CSS del `Chip`. El tono no: estos dos son controles,
 * así que su eje es `ActionTone` —el del `Button`—, no el `ChipTone` del chip inerte. La diferencia
 * es `success`: un chip puede decir «reportando», pero «salió bien» no es algo que se pueda
 * accionar. Lo demás que agregan es la capa de interacción.
 */

export interface ChipButtonProps extends ComponentProps<typeof BaseButton> {
  /** Default `info`, igual que el `Chip`. El eje es `ActionTone`: sin `success`, porque esto se
   *  acciona. */
  tone?: ActionTone
  /**
   * Default `soft` — y acá los dos interactivos **no** siguen al `Chip`, que es `subtle`.
   *
   * No es inconsistencia: el hover de un chip control marca la línea y deja el fondo quieto
   * (ADR-0008 regla 4), y una línea no se puede marcar si en reposo no está. Con `subtle` el hover
   * tendría que estrenar el borde, que es un salto de geometría en vez de un refuerzo. El borde en
   * reposo es además la única señal de que la cosa se toca.
   */
  variant?: ChipVariant
}

/**
 * Un chip que se puede accionar: un filtro que se prende y se apaga, una sugerencia que se elige,
 * una etiqueta que se aplica.
 *
 * Sale del `Button` de Base UI y no de un `<button>` pelado por lo mismo que el `Button` de la
 * capa: expone `data-disabled` para estilar sin depender de `:disabled`, se compone con el
 * `render` de los triggers sin anidar un botón dentro de otro, y permite `focusableWhenDisabled`
 * —que acá también viene activado, así un chip deshabilitado sigue siendo alcanzable con teclado
 * y puede llevar un `Tooltip` que explique por qué no está disponible.
 */
// `forwardRef` es un shim de React 18: la capa corre React 19, donde la `ref` ya viaja dentro de
// `props`. Se retira con el codemod `react/19/remove-forward-ref` cuando el último consumidor
// migre (ADR-0028).
export const ChipButton = forwardRef<HTMLElement, ChipButtonProps>(function ChipButton(
  { tone = 'info', variant = 'soft', focusableWhenDisabled = true, className, ...props },
  ref,
) {
  return (
    <BaseButton
      ref={ref}
      className={chipControlClasses(tone, variant, className)}
      focusableWhenDisabled={focusableWhenDisabled}
      {...props}
    />
  )
})

ChipButton.displayName = 'ChipButton'

export interface ChipLinkProps extends useRender.ComponentProps<'a'> {
  /** Default `primary`: un chip que navega se ofrece, y el tono sin color no se ofrece. */
  tone?: ActionTone
  /** Default `soft`, por el mismo motivo que en `ChipButton`. */
  variant?: ChipVariant
  /**
   * El icono de redirección al final. Va por default porque es lo que distingue a este chip de
   * uno que solo se acciona: sin él, nada en la forma dice que al tocarlo te vas a otro lado.
   * Se apaga cuando el destino ya está dicho por el texto.
   */
  icon?: boolean
}

/**
 * Un chip que navega. Emite un `<a href>` real, así que el clic del medio, «abrir en pestaña
 * nueva» y el menú contextual funcionan solos — cosas que un `onClick` sobre un `<button>` no da.
 *
 * El destino puede llegar de dos formas: `href` para una URL, o `render` para componerlo con el
 * `Link` de un router. Se usa `useRender` de Base UI en vez de importar el router acá porque
 * **`au/ui` no depende de ninguno**: la capa se porta entera a otra aplicación, y una dependencia
 * de routing la haría intransportable. Quien tenga router pasa su `Link` por `render`.
 *
 * **Requiere destino, y nada lo verifica.** Un `<a>` sin `href` ni `render` se dibuja y se siente
 * como un control, pero no entra al orden de tabulación ni responde a Enter — se ve interactivo y no
 * lo es, que es peor que no estar. El tipo admite los dos ausentes porque `href` y `render` son dos
 * caminos válidos y ninguno es obligatorio por separado, así que la regla la sostiene quien escribe
 * el call site. No hay un aviso de desarrollo que la vigile: para saber si está en desarrollo
 * tendría que leer un global del bundler, y una capa que se porta a otra aplicación no puede
 * suponer cuál la empaqueta.
 *
 * **No se puede deshabilitar.** Un `<a>` con `disabled` se ve apagado y el `href` sigue navegando.
 * Si el destino no está disponible, lo que corresponde es no renderizar el chip o usar un
 * `ChipButton` sin acción.
 */
export function ChipLink({
  tone = 'primary',
  variant = 'soft',
  icon = true,
  className,
  children,
  render,
  ...props
}: ChipLinkProps) {
  return useRender({
    render,
    defaultTagName: 'a',
    props: {
      ...props,
      className: chipControlClasses(tone, variant, className),
      children: (
        <>
          {children}
          {icon && <IconArrowUpRight className={styles['au-chip-icon']} />}
        </>
      ),
    },
  })
}
